import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Shield,
  Users,
  Home,
  Clock,
  BarChart3,
  FileText,
  MessageCircle,
  CheckCircle2,
  Sparkles,
  ChevronRight,
  Smartphone,
  Lock,
  Bell,
  Zap,
  CalendarCheck,
  Package,
  CreditCard,
  PieChart,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import PublicNavbar from "@/components/landing/PublicNavbar";
import PublicFooter from "@/components/lgpd/Footer";
import itacondoImg from "@/assets/products/itacondo.png";

const WHATSAPP_URL =
  "https://web.whatsapp.com/send?phone=5521998231720&text=" +
  encodeURIComponent(
    "Olá! Gostaria de conhecer o sistema de Gestão de Condomínios da itaCondo.",
  );

const MODULES = [
  {
    icon: Shield,
    title: "Portaria Inteligente",
    desc: "Registro de encomendas, correspondências e comunicação em tempo real com moradores.",
    tone: "from-blue-500 to-indigo-500",
  },
  {
    icon: Users,
    title: "Controle de Visitantes",
    desc: "Cadastro, autorização e histórico com notificação automática para morador e porteiro.",
    tone: "from-emerald-500 to-teal-500",
  },
  {
    icon: Home,
    title: "Áreas Comuns",
    desc: "Reserva com regras configuráveis, calendário visual e aprovação automática.",
    tone: "from-amber-500 to-orange-500",
  },
  {
    icon: Clock,
    title: "Ponto Eletrônico",
    desc: "Registro com foto, geolocalização, banco de horas e folha de ponto completa.",
    tone: "from-violet-500 to-purple-500",
  },
  {
    icon: BarChart3,
    title: "Financeiro Completo",
    desc: "Cobranças, despesas, inadimplência e dashboards financeiros por unidade.",
    tone: "from-rose-500 to-pink-500",
  },
  {
    icon: FileText,
    title: "Relatórios em PDF",
    desc: "Relatórios gerenciais detalhados com indicadores, ocorrências e movimentações.",
    tone: "from-cyan-500 to-sky-500",
  },
  {
    icon: Package,
    title: "Encomendas",
    desc: "Registro com foto, notificação push ao morador e baixa rastreável na retirada.",
    tone: "from-fuchsia-500 to-purple-500",
  },
  {
    icon: CalendarCheck,
    title: "Assembleias e Reuniões",
    desc: "Agendamento, atas, convocações e registro de presenças.",
    tone: "from-teal-500 to-emerald-500",
  },
  {
    icon: CreditCard,
    title: "Cobranças Automáticas",
    desc: "Geração de boletos, controle de pagamentos e regras de juros e multas.",
    tone: "from-indigo-500 to-blue-500",
  },
];

const HIGHLIGHTS = [
  { icon: Smartphone, label: "App PWA para Morador, Síndico e Porteiro" },
  { icon: Lock, label: "Multi-tenant com isolamento total por condomínio" },
  { icon: Bell, label: "Notificações push em tempo real" },
  { icon: Zap, label: "Performance otimizada e infraestrutura em nuvem" },
];

const PERFIS = [
  {
    titulo: "Síndicos",
    desc: "Visão 360º do condomínio, dashboards, financeiro, ocorrências e relatórios em um só painel.",
  },
  {
    titulo: "Moradores",
    desc: "App leve para reservar áreas, ver encomendas, abrir ocorrências, receber avisos e autorizar visitas.",
  },
  {
    titulo: "Porteiros",
    desc: "Busca global por morador, registro de visitantes com foto, encomendas e comunicação direta.",
  },
  {
    titulo: "Colaboradores",
    desc: "Ponto eletrônico com foto e geolocalização, banco de horas e folha do mês.",
  },
];

const GestaoCondominios = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Gestão de Condomínios | itaCondo Sistemas";
    const meta =
      document.querySelector<HTMLMetaElement>('meta[name="description"]') ||
      (() => {
        const m = document.createElement("meta");
        m.name = "description";
        document.head.appendChild(m);
        return m;
      })();
    const prevDesc = meta.content;
    meta.content =
      "Plataforma completa de gestão de condomínios: portaria, visitantes, áreas comuns, ponto eletrônico, financeiro e relatórios. App para síndico, morador e porteiro.";
    return () => {
      document.title = prevTitle;
      meta.content = prevDesc;
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <PublicNavbar />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="pointer-events-none absolute -top-[20%] -right-[10%] h-[700px] w-[700px] rounded-full bg-primary/20 blur-[120px]" />
        <div className="pointer-events-none absolute top-[40%] -left-[10%] h-[600px] w-[600px] rounded-full bg-blue-500/15 blur-[120px]" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8 flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-blue-300">Gestão de Condomínios</span>
          </nav>

          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Copy */}
            <div className="flex flex-col gap-7">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-sm">
                <span className="flex size-2 animate-pulse rounded-full bg-gradient-to-r from-primary to-blue-500" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-white">
                  PRODUTO · Plataforma proprietária
                </span>
              </div>

              <h1 className="text-5xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
                <span className="block">Gestão de</span>
                <span className="block bg-gradient-to-r from-primary via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Condomínios.
                </span>
              </h1>

              <p className="max-w-xl text-lg leading-relaxed text-slate-200 sm:text-xl">
                Eficiência total para síndicos, conforto absoluto para moradores.
                Tecnologia, transparência e controle em um só app — portaria,
                visitantes, áreas comuns, financeiro e relatórios.
              </p>

              <div className="flex flex-wrap items-center gap-4">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="group h-14 rounded-full bg-green-500 px-8 text-base font-bold text-white shadow-lg shadow-green-500/30 transition-all hover:scale-105 hover:bg-green-600"
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Falar no WhatsApp
                  </Button>
                </a>
                <Link to="/login">
                  <Button
                    size="lg"
                    variant="ghost"
                    className="h-14 rounded-full border border-white/15 bg-white/5 px-8 text-base font-bold text-white backdrop-blur-sm hover:border-white/30 hover:bg-white/10"
                  >
                    Acessar plataforma
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>

              {/* Highlights */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                {HIGHLIGHTS.map((h) => {
                  const Icon = h.icon;
                  return (
                    <div
                      key={h.label}
                      className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur-sm"
                    >
                      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary to-blue-500 text-white">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1 text-xs font-medium text-slate-200">
                        {h.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Image */}
            <div className="relative w-full lg:h-[600px]">
              <div className="relative z-10 h-[360px] w-full overflow-hidden rounded-[2.5rem] border border-white/10 bg-slate-900 shadow-2xl sm:h-[460px] lg:h-full">
                <img
                  src={itacondoImg}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full scale-110 object-cover object-center opacity-60 blur-2xl saturate-150"
                />
                <div className="absolute inset-0 z-10 bg-slate-900/50" />
                <div className="absolute inset-0 z-10 bg-gradient-to-br from-primary/15 via-transparent to-blue-500/15" />
                <img
                  src={itacondoImg}
                  alt="Plataforma itaCondo — Gestão de Condomínios"
                  className="absolute inset-0 z-20 h-full w-full object-contain object-center p-4 sm:p-6"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-40 bg-gradient-to-t from-slate-900/80 to-transparent" />

                <div className="absolute bottom-6 left-6 right-6 z-30 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-xl sm:bottom-8 sm:left-8 sm:right-8 sm:p-5">
                  <div className="grid size-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary to-blue-500 text-white shadow-lg">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-white">
                      itaCondo · Plataforma Completa
                    </p>
                    <p className="truncate text-xs text-slate-300">
                      Síndico · Morador · Porteiro · Colaborador
                    </p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 -z-10 hidden h-full w-full rounded-[2.5rem] border-2 border-primary/30 lg:block" />
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="relative py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-4 py-2">
              <Sparkles className="h-4 w-4 text-blue-400" />
              <span className="text-sm text-blue-300">Tudo em um só lugar</span>
            </div>
            <h2 className="mb-4 text-3xl font-bold leading-tight text-white lg:text-5xl">
              Módulos pensados para o{" "}
              <span className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent">
                dia a dia do condomínio
              </span>
            </h2>
            <p className="text-lg text-slate-300">
              Cada módulo resolve uma dor real — sem planilhas, sem retrabalho e
              sem perder informação.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {MODULES.map((m) => {
              const Icon = m.icon;
              return (
                <Card
                  key={m.title}
                  className="group border-white/10 bg-slate-800/50 backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-blue-500/40 hover:bg-slate-800/80"
                >
                  <CardContent className="p-6">
                    <div
                      className={`mb-4 grid size-12 place-items-center rounded-xl bg-gradient-to-br ${m.tone} text-white shadow-lg`}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mb-2 text-lg font-bold text-white">
                      {m.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-slate-300">
                      {m.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* PERFIS */}
      <section className="relative bg-slate-800/40 py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold text-white lg:text-5xl">
              Um app para cada perfil
            </h2>
            <p className="text-lg text-slate-300">
              Dashboards e fluxos otimizados para cada papel dentro do condomínio.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PERFIS.map((p, i) => (
              <Card
                key={p.titulo}
                className="border-white/10 bg-slate-900/60 backdrop-blur-sm"
              >
                <CardContent className="p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="grid size-10 place-items-center rounded-lg bg-gradient-to-br from-primary to-blue-500 text-sm font-extrabold text-white">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-lg font-bold text-white">{p.titulo}</h3>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-300">
                    {p.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section className="relative py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-4 py-2">
                <Star className="h-4 w-4 text-blue-400" />
                <span className="text-sm text-blue-300">Por que itaCondo</span>
              </div>
              <h2 className="mb-6 text-3xl font-bold text-white lg:text-5xl">
                Tecnologia, transparência e{" "}
                <span className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent">
                  controle total
                </span>
              </h2>
              <ul className="space-y-4">
                {[
                  "Plataforma proprietária, em evolução contínua",
                  "Arquitetura multi-tenant com isolamento total por condomínio (RLS)",
                  "Acesso por web e app PWA (instala em qualquer celular)",
                  "Notificações push em tempo real",
                  "Relatórios em PDF prontos para assembleia",
                  "Suporte humano e onboarding incluído",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-400" />
                    <span className="text-base text-slate-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Card className="border-blue-500/30 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="mb-6 flex items-center gap-4">
                  <div className="grid size-14 place-items-center rounded-2xl bg-gradient-to-br from-primary to-blue-500 text-white shadow-lg">
                    <PieChart className="h-7 w-7" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-blue-300">
                      Resultado típico
                    </p>
                    <p className="text-lg font-bold text-white">
                      Em até 30 dias de uso
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { v: "-70%", l: "tempo de atendimento" },
                    { v: "+90%", l: "satisfação dos moradores" },
                    { v: "100%", l: "rastreabilidade" },
                    { v: "24/7", l: "acesso pelo celular" },
                  ].map((m) => (
                    <div
                      key={m.l}
                      className="rounded-xl border border-white/10 bg-white/5 p-4 text-center"
                    >
                      <p className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-3xl font-extrabold text-transparent">
                        {m.v}
                      </p>
                      <p className="mt-1 text-xs text-slate-300">{m.l}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-blue-500/30 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 shadow-2xl">
            {/* Glows decorativos atrás (não clareiam o card) */}
            <div className="pointer-events-none absolute -top-32 -right-24 h-72 w-72 rounded-full bg-primary/30 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-blue-500/20 blur-[120px]" />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:48px_48px]" />

            <div className="relative p-8 text-center lg:p-14">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5">
                <Sparkles className="h-3.5 w-3.5 text-blue-300" />
                <span className="text-xs font-semibold uppercase tracking-wider text-blue-200">
                  Demonstração gratuita
                </span>
              </div>
              <h2 className="mx-auto mb-4 max-w-3xl text-2xl font-bold leading-tight text-white lg:text-4xl">
                Pronto para modernizar a gestão do seu condomínio?
              </h2>
              <p className="mx-auto mb-8 max-w-2xl text-base text-slate-300 lg:text-lg">
                Receba uma demonstração ao vivo e veja como o itaCondo se adapta
                ao seu condomínio em minutos.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Button
                    size="lg"
                    className="h-14 rounded-full bg-green-500 px-8 text-base font-bold text-white shadow-lg shadow-green-500/30 transition-all hover:scale-105 hover:bg-green-600"
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Solicitar demonstração
                  </Button>
                </a>
                <Link to="/#contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 rounded-full border-white/25 bg-white/5 px-8 text-base font-bold text-white backdrop-blur-sm hover:border-white/40 hover:bg-white/10 hover:text-white"
                  >
                    Enviar mensagem
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default GestaoCondominios;